<script setup lang="ts">
import type { LinkProps } from '@nuxt/ui';
import type { NoticeProps } from './Notice.vue';
import { hasProtocol } from 'ufo';
import { computed } from 'vue';

export interface TextLinkProps extends Pick<LinkProps, 'to' | 'href' | 'target' | 'rel' | 'external'> {
	text?: string;
	color?: NoticeProps['color'];
	ui?: { trailingIcon?: string };
}

// DLink renders a bare anchor, so the link inherits the surrounding font size instead of
// forcing one. Color has no DLink prop, so it maps to text utilities below.
const { to, href, target, rel, external, text, color = 'neutral', ui } = defineProps<TextLinkProps>();

// Mirror DLink's own routing decision (it uses the same hasProtocol call) so the
// icon and new-tab behavior can never disagree with how DLink actually routes.
const isExternal = computed(() => {
	const path = to ?? href;

	return external || target === '_blank' || (typeof path === 'string' && hasProtocol(path, { acceptRelative: true }));
});

const icon = computed(() => (isExternal.value ? 'lucide:external-link' : undefined));

const linkTarget = computed(() => target ?? (isExternal.value ? '_blank' : undefined));

const linkRel = computed(() => rel ?? (isExternal.value ? 'noopener noreferrer' : undefined));

const neutralColorClass = 'text-muted hover:text-default';

const colorClassMap: Record<string, string> = {
	primary: 'text-primary-800 dark:text-primary-200 hover:text-primary-900 dark:hover:text-primary-100',
	info: 'text-info-800 dark:text-info-200 hover:text-info-900 dark:hover:text-info-100',
	success: 'text-success-800 dark:text-success-200 hover:text-success-900 dark:hover:text-success-100',
	warning: 'text-warning-800 dark:text-warning-200 hover:text-warning-900 dark:hover:text-warning-100',
	error: 'text-error-800 dark:text-error-200 hover:text-error-900 dark:hover:text-error-100',
};

const colorClass = computed(() => colorClassMap[color] ?? neutralColorClass);
</script>

<template>
	<DLink
		:to
		:href
		:external
		:target="linkTarget"
		:rel="linkRel"
		class="underline underline-offset-4"
		:class="colorClass"
	>
		<slot>{{ text }}</slot>
		<DIcon v-if="icon" :name="icon" class="ms-1 inline-block size-4 align-middle" :class="ui?.trailingIcon" />
	</DLink>
</template>
